import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import loanService from '../../services/loanService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrency } from '../../contexts/CurrencyContext';

import { format } from 'date-fns';

const LoanList = () => {
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
 // const { showError } = useToast();
const { showSuccess, showError } = useToast(); 
const { formatCurrency } = useCurrency();
  useEffect(() => {
    fetchLoans();
  }, []);

  useEffect(() => {
    filterLoans();
  }, [searchTerm, statusFilter, loans]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const data = await loanService.getAllLoans();
      setLoans(data.loans);
    } catch (error) {
      showError('Erreur lors du chargement des prêts');
    } finally {
      setLoading(false);
    }
  };

  const filterLoans = () => {
    let filtered = loans;

    if (searchTerm) {
      filtered = filtered.filter(loan =>
        (loan.loan_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (loan.client?.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (loan.client?.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (loan.client?.client_number || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(loan => loan.status === statusFilter);
    }

    setFilteredLoans(filtered);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Approuvé', color: 'bg-blue-100 text-blue-800' },
      disbursed: { label: 'Décaissé', color: 'bg-purple-100 text-purple-800' },
      active: { label: 'Actif', color: 'bg-green-100 text-green-800' },
      completed: { label: 'Terminé', color: 'bg-gray-100 text-gray-800' },
      defaulted: { label: 'Défaillant', color: 'bg-red-100 text-red-800' },
    };
    return configs[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const handleApprove = async (id) => {
    try {
      await loanService.approveLoan(id, {});
      showSuccess('Prêt approuvé avec succès');
      fetchLoans();
    } catch (error) {
      showError('Erreur lors de l\'approbation du prêt');
    }
  };

  const handleDisburse = async (id) => {
    try {
      await loanService.disburseLoan(id, {});
      showSuccess('Prêt décaissé avec succès');
      fetchLoans();
    } catch (error) {
      showError('Erreur lors du décaissement du prêt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des prêts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Prêts</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestion du portefeuille de prêts
          </p>
        </div>
        <button
          onClick={() => navigate('/loans/new')}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouveau Prêt
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-blue-100 p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Prêts</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(loans.reduce((sum, loan) => sum + (loan.amount || 0), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-green-100 p-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Prêts Actifs</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {loans.filter(l => l.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-yellow-100 p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {loans.filter(l => l.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-red-100 p-3">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Défaillants</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {loans.filter(l => l.status === 'defaulted').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Rechercher
              </label>
              <input
                type="text"
                id="search"
                className="input-field mt-1"
                placeholder="N° prêt, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select
                id="status"
                className="input-field mt-1"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="disbursed">Décaissé</option>
                <option value="active">Actif</option>
                <option value="completed">Terminé</option>
                <option value="defaulted">Défaillant</option>
              </select>
            </div>

            <div>
              <label htmlFor="product" className="block text-sm font-medium text-gray-700">
                Produit
              </label>
              <select id="product" className="input-field mt-1">
                <option value="all">Tous les produits</option>
                <option value="agriculture">Agriculture</option>
                <option value="commerce">Commerce</option>
                <option value="education">Éducation</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <div className="card">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Prêt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Début
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLoans.map((loan) => {
                  const status = getStatusConfig(loan.status);
                  return (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {loan.loan_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {loan.client
                            ? `${loan.client.first_name || ''} ${loan.client.last_name || ''}`.trim()
                            : 'Client non renseigné'}
                        </div>
                        {loan.client?.client_number && (
                          <div className="text-xs text-gray-500">{loan.client.client_number}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(loan.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {loan.term} mois
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {loan.start_date ? format(new Date(loan.start_date), 'dd/MM/yyyy') : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/loans/${loan.id}`)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Voir détails"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {loan.status === 'pending' && (
                            <button
                              onClick={() => handleApprove(loan.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Approuver"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                          {loan.status === 'approved' && (
                            <button
                              onClick={() => handleDisburse(loan.id)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Décaisser"
                            >
                              <CurrencyDollarIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredLoans.length === 0 && (
              <div className="text-center py-12">
                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun prêt trouvé</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Essayez de modifier vos critères de recherche.'
                    : 'Commencez par créer votre premier prêt.'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <div className="mt-6">
                    <button
                      onClick={() => navigate('/loans/new')}
                      className="btn-primary"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Créer un prêt
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanList;
